import { useState, useRef } from "react";
import { Upload, FileSpreadsheet, CheckCircle2, AlertTriangle, Download, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const FIELDS = [
  { key: "name", label: "Nome" },
  { key: "whatsapp", label: "WhatsApp" },
  { key: "email", label: "E-mail" },
  { key: "phone", label: "Telefone" },
  { key: "birth_date", label: "Data de nascimento" },
  { key: "notes", label: "Observação" },
];

export default function ImportContacts() {
  const fileRef = useRef();
  const [preview, setPreview] = useState(null);
  const [mapping, setMapping] = useState({});
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const onFile = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setLoading(true); setResult(null);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/import/preview", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setPreview(data);
      setMapping(data.mapping || {});
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setLoading(false); }
  };

  const confirm = async () => {
    setLoading(true);
    try {
      const { data } = await api.post(`/import/${preview.import_id}/confirm`, { mapping });
      setResult(data);
      toast.success(`${data.new_count} novos clientes importados!`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setLoading(false); }
  };

  const exportInvalid = () => {
    if (!result?.invalid_numbers?.length) return;
    const csv = "NOME,NUMERO\n" + result.invalid_numbers.map((n) => `"${n.name}","${n.number}"`).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = "numeros_invalidos.csv"; a.click();
  };

  return (
    <div className="space-y-6 animate-fade-up max-w-4xl" data-testid="import-page">
      <div>
        <h1 className="font-display text-2xl sm:text-3xl font-bold text-slate-900">Importar Contatos</h1>
        <p className="text-sm text-slate-500 mt-1">Importe clientes de planilhas XLSX, XLS ou CSV. Não duplicamos pelo WhatsApp.</p>
      </div>

      {!preview && (
        <div onClick={() => fileRef.current?.click()} className="bg-white rounded-xl border-2 border-dashed border-slate-300 hover:border-emerald-400 p-12 text-center cursor-pointer transition-colors" data-testid="import-dropzone">
          <input ref={fileRef} type="file" accept=".xlsx,.xls,.csv" className="hidden" onChange={onFile} data-testid="import-file-input" />
          <div className="h-14 w-14 rounded-2xl bg-emerald-50 flex items-center justify-center mx-auto mb-4">
            <Upload className="h-7 w-7 text-emerald-600" />
          </div>
          <p className="font-medium text-slate-700">{loading ? "Lendo arquivo..." : "Clique para selecionar sua planilha"}</p>
          <p className="text-xs text-slate-400 mt-1">Formatos aceitos: XLSX, XLS, CSV</p>
          <p className="text-xs text-slate-400 mt-3">Exemplo: NOME | WHATSAPP | EMAIL | DATA_NASCIMENTO | OBSERVAÇÃO</p>
        </div>
      )}

      {preview && !result && (
        <>
          <div className="bg-white rounded-xl border border-slate-200 p-6 shadow-sm">
            <div className="flex items-center gap-3 mb-4">
              <FileSpreadsheet className="h-5 w-5 text-emerald-600" />
              <div><p className="font-semibold text-slate-800">{preview.total} registros encontrados</p><p className="text-xs text-slate-400">Mapeie as colunas do sistema com as colunas da planilha.</p></div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {FIELDS.map((f) => (
                <div key={f.key} className="flex items-center gap-2">
                  <span className="text-sm text-slate-600 w-32">{f.label}</span>
                  <ArrowRight className="h-4 w-4 text-slate-300" />
                  <Select value={mapping[f.key]?.toString() ?? "none"} onValueChange={(v) => setMapping((m) => ({ ...m, [f.key]: v === "none" ? undefined : parseInt(v) }))}>
                    <SelectTrigger className="flex-1" data-testid={`map-${f.key}`}><SelectValue placeholder="—" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">— Ignorar —</SelectItem>
                      {preview.headers.map((h, i) => <SelectItem key={i} value={i.toString()}>{h}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
            <p className="px-4 py-3 text-sm font-semibold text-slate-700 bg-slate-50">Prévia (primeiras linhas)</p>
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead><tr className="bg-slate-50 text-slate-500">{preview.headers.map((h, i) => <th key={i} className="px-3 py-2 text-left font-medium">{h}</th>)}</tr></thead>
                <tbody className="divide-y divide-slate-100">
                  {preview.sample.map((row, i) => <tr key={i}>{preview.headers.map((_, j) => <td key={j} className="px-3 py-2 text-slate-600 whitespace-nowrap">{row[j] || "—"}</td>)}</tr>)}
                </tbody>
              </table>
            </div>
          </div>

          <div className="flex gap-2">
            <Button variant="outline" onClick={() => setPreview(null)}>Cancelar</Button>
            <Button onClick={confirm} disabled={loading} className="bg-emerald-500 hover:bg-emerald-600" data-testid="import-confirm-button">
              {loading ? "Importando..." : "Confirmar Importação"}
            </Button>
          </div>
        </>
      )}

      {result && (
        <div className="bg-white rounded-xl border border-slate-200 p-6 shadow-sm space-y-5" data-testid="import-result">
          <div className="flex items-center gap-3"><CheckCircle2 className="h-6 w-6 text-emerald-600" /><h3 className="font-display text-xl font-bold text-slate-900">Importação concluída</h3></div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <Stat label="Novos clientes" value={result.new_count} color="text-emerald-600" />
            <Stat label="Já cadastrados" value={result.dup_count} color="text-slate-500" />
            <Stat label="Números válidos" value={result.valid_count} color="text-emerald-600" />
            <Stat label="Números inválidos" value={result.invalid_count} color="text-red-500" />
          </div>
          {result.invalid_count > 0 && (
            <div className="flex items-center gap-3 p-3 rounded-lg bg-amber-50 border border-amber-100">
              <AlertTriangle className="h-5 w-5 text-amber-600" />
              <p className="text-sm text-amber-800 flex-1">{result.invalid_count} números inválidos foram identificados.</p>
              <Button size="sm" variant="outline" onClick={exportInvalid} data-testid="export-invalid-button"><Download className="h-4 w-4 mr-1" /> Exportar inválidos</Button>
            </div>
          )}
          <Button onClick={() => { setPreview(null); setResult(null); }} className="bg-emerald-500 hover:bg-emerald-600">Importar outra planilha</Button>
        </div>
      )}
    </div>
  );
}

function Stat({ label, value, color }) {
  return <div className="text-center p-4 rounded-lg bg-slate-50 border border-slate-100"><p className={`text-2xl font-bold font-display ${color}`}>{value}</p><p className="text-xs text-slate-500 mt-1">{label}</p></div>;
}
